import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';
import { getListAction } from './pageSlice'; // For business unit search using old API

const initialState = {};

export const projectSliceV2 = createSlice({
	name: 'projectStateV2',
	initialState,
	reducers: {},
});

/**
 * Search Projects V2
 * Endpoint: POST /api/v2/search
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria (all optional)
 *   - project_code (string): Partial match on project code
 *   - project_name (string): Partial match on project name
 *   - client_id (integer): Filter by client
 *   - business_unit_id (integer): Filter by business unit
 *   - project_category (string): Enum ["regular", "adhoc", "intercity"]
 *   - gst_number (string): Filter by GST number
 *   - pod_required (boolean): Filter by POD requirement
 *   - pin_code (string): Filter by pincode
 *   - zone (string): Filter by zone
 *   - city (string): Filter by city
 *   - state (string): Filter by state
 *   - cost_center_id (integer): Filter by cost center
 *   - temperature_control (boolean): Filter by temperature control
 *   - adhoc_approval_required (boolean): Filter by adhoc approval requirement
 *   - is_active (boolean): Filter by active status
 *   - created_from (string): ISO date - Filter projects created after this date
 *   - created_to (string): ISO date - Filter projects created before this date
 * @param {Object} params.pagination - Pagination settings
 *   - page (integer): Page number (default: 1)
 *   - per_page (integer): Items per page (default: 20)
 * @param {Object} params.sort_by - Sorting settings
 *   - field (string): Sort field (default: "created_at")
 *   - order (string): Sort order "asc" or "desc" (default: "desc")
 * @returns {Promise} Object with { rows, pagination, count, filters }
 */
export const getProjectListV2 =
	({ filter_by = {}, pagination = {} }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					// Map search_term to query_str as expected by V2 API
					if (key === 'search_term') {
						cleanFilterBy.query_str = filter_by[key];
					} else {
						cleanFilterBy[key] = filter_by[key];
					}
				}
			});
		}

		// Use pagination object for V2 API
		const page = pagination.page || 1;
		const per_page = pagination.per_page || 20;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'project',
				pagination: {
					page,
					per_page,
				},
				filter_by: cleanFilterBy,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			rows: response?.data?.projects || [],
			count: response?.data?.pagination?.total,
			filters: response?.data?.filters_applied || {},
		}));
	};

// Get single project by ID
export const getProjectByIdV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'project',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const projects = response?.data?.projects || [];
			return projects.length > 0 ? projects[0] : null;
		});
	};

// Create project V2
export const createProjectV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'project',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			const projectCode = res?.data?.project_code;
			const message = projectCode
				? `Project created successfully with code: ${projectCode}`
				: 'Project created successfully';
			toaster.success(message);
			return res;
		});
	};

// Update project V2
export const updateProjectV2 =
	({ id, req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'project',
			data: {
				id,
				...req_data,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Project updated successfully');
			return res;
		});
	};

// Verify project V2
export const verifyProjectV2 =
	({ id }) =>
	async () => {
		const data = {
			update_type: 'project_verify',
			data: {
				project_id: id,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Project Verified Successfully');
			return res;
		});
	};

/**
 * Export project list to CSV
 * Endpoint: POST /api/v2/generate-report
 * Payload shape kept same as v1 export API.
 */
export const exportProjectCSV =
	({ data = {} }) =>
	async () => {
		const CREDENTIALS = {
			url: '/generate-report',
			data: {
				report_type: 'project_export',
				file_type: 'csv',
				data,
			},
			version: 'v2',
			aclBlockApiCall: false,
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.data?.download_url) {
				window.open(response.data.download_url);
			}
			return response;
		});
	};

// Search business units for dropdown using old API (pageSlice getListAction)
export const searchBusinessUnitsV2 =
	({ filter_by = {}, limit = 100 }) =>
	async (dispatch) => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		return dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: limit || 100,
				offset: 0,
				...cleanFilterBy,
			})
		).then((response) => ({
			results: (response?.rows || []).map((item) => ({
				id: item.id,
				label: item.name || item.business_unit_name,
				value: item.id,
			})),
			total: response?.count || 0,
		}));
	};

// Search cost centers for dropdown using search_type
export const searchCostCentersV2 =
	({ filter_by = {}, limit = 100 }) =>
	async () => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'cost_center',
				filter_by: cleanFilterBy,
				limit: limit || 100,
				offset: 0,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: (response?.data?.cost_centers || []).map((item) => ({
				id: item.id,
				label: item.name || item.cost_center_name,
				value: item.id,
			})),
			total: response?.data?.pagination?.total || 0,
		}));
	};

export default projectSliceV2.reducer;
